import type { AnyFSA } from '@makemydeal/dr-browser-utils';

import type { ReduxMiddleware, ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';
import type { MiddlewareManager } from '@makemydeal/dr-activities-role-assumption';
import { INITIALIZING_ROLE_ASSUMPTION, startRoleAssumption } from '@makemydeal/dr-activities-role-assumption';
import { SALES_CHECKIN_APP_PREFIX, startLoadingIndicatorDisappearsClock } from '@makemydeal/dr-shared-ui-utils';
import { evaluate, hideGlobalSpinner, setWorkflowStatus } from '@makemydeal/dr-sales-checkin-store';

import { configureRoleAssumption } from './config';
import { fetchBootstrapDataWorker } from '../../actions/bootstrapAppActionCreators';

import type { SalesCheckinRootState } from '@makemydeal/dr-sales-checkin-store';

// keep a cached version of the middleware once we're able to build it.
// exported to support testing
export let cachedRoleAssumptionMiddlewareMgr: MiddlewareManager | undefined;

// needed for testing
export const roleAssumptionMiddlewareClearCache = () => (cachedRoleAssumptionMiddlewareMgr = undefined);

// Tracks whether the in-flight bootstrap re-fetch below was triggered by role assumption, so its
// BOOTSTRAP_SUCCESS/BOOTSTRAP_FAILURE can be told apart from the app's own first bootstrap and
// given the hide-spinner + navigation hand-off.
let isRoleAssumptionInitInFlight = false;

export const roleAssumptionMiddleware: ReduxMiddleware<SalesCheckinRootState> =
    (store: ReduxStore<SalesCheckinRootState>) => (next: ReduxNext) => (action: AnyFSA) => {
        switch (action.type) {
            case bootstrapActionTypes.BOOTSTRAP_SUCCESS: {
                cachedRoleAssumptionMiddlewareMgr = configureRoleAssumption();

                if (isRoleAssumptionInitInFlight) {
                    isRoleAssumptionInitInFlight = false;
                    store.dispatch(hideGlobalSpinner());
                    // Promotes the assumed user's now-current state to a destination, same as
                    // useRoleAssumptionNavigation's Step 3 (reEvaluateWorkflow) used to do.
                    store.dispatch(setWorkflowStatus(evaluate(store.getState())));
                    store.dispatch(startRoleAssumption());
                }
                break;
            }
            case bootstrapActionTypes.BOOTSTRAP_FAILURE: {
                if (isRoleAssumptionInitInFlight) {
                    isRoleAssumptionInitInFlight = false;
                    store.dispatch(hideGlobalSpinner());
                }
                break;
            }
            case INITIALIZING_ROLE_ASSUMPTION: {
                // Re-runs the app's own existing init API call (fetchBootstrapDataWorker — the same
                // one Bootstrapper.tsx uses) as the assumed user. sessionStorage already carries the
                // assumed username, so the fetch/axios interceptor stamps the role-assumption header
                // automatically.
                isRoleAssumptionInitInFlight = true;
                startLoadingIndicatorDisappearsClock();
                store.dispatch(fetchBootstrapDataWorker(SALES_CHECKIN_APP_PREFIX));

                break;
            }
        }

        if (cachedRoleAssumptionMiddlewareMgr) {
            cachedRoleAssumptionMiddlewareMgr.middleware(store)(next)(action);
        } else {
            next(action);
        }
    };
